/** Rows added to the timeline per vertical page. */
export const RESOURCE_PAGE_SIZE = 30;

/** Distance from the bottom of the row viewport that counts as "at the bottom". */
export const RESOURCE_SCROLL_THRESHOLD_PX = 120;

export interface VerticalScrollMetrics {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

export function isNearVerticalBottom(
  { scrollTop, clientHeight, scrollHeight }: VerticalScrollMetrics,
  threshold: number = RESOURCE_SCROLL_THRESHOLD_PX,
): boolean {
  if (!Number.isFinite(scrollTop) || !Number.isFinite(clientHeight) || !Number.isFinite(scrollHeight)) return false;
  // A viewport taller than its content can never be scrolled to a bottom the
  // user has not already reached, so it must not pull another page in.
  if (scrollHeight <= clientHeight) return false;
  return scrollHeight - (scrollTop + clientHeight) <= threshold;
}

/** Whether the loaded rows are too short to scroll, so no scroll event can ever fire. */
export function isViewportUnderfilled({ clientHeight, scrollHeight }: VerticalScrollMetrics): boolean {
  if (!Number.isFinite(clientHeight) || !Number.isFinite(scrollHeight)) return false;
  return scrollHeight <= clientHeight;
}

interface ResourceRowNode {
  id: string | number;
  expanded?: boolean;
  children?: ResourceRowNode[];
}

export interface ResourceRowIndex {
  /**
   * Every row id in the tree, so events can be limited to the rows the scheduler
   * actually holds: an event on a row outside the loaded window costs a record,
   * an index entry and a diff pass while rendering nothing.
   */
  rowIds: Set<string>;
  /** Whether each leaf row takes the alternate background, by row id. */
  altShading: Map<string, boolean>;
}

export function createResourceRowIndex(nodes: readonly ResourceRowNode[]): ResourceRowIndex {
  const rowIds = new Set<string>();
  const altShading = new Map<string, boolean>();
  let leafIndex = 0;

  /**
   * `shaded` is false below a collapsed group: its rows are in the store but off
   * screen, so shading them would offset the stripes of every row below it.
   */
  const visit = (list: readonly ResourceRowNode[], shaded: boolean): void => {
    for (const node of list) {
      const id = String(node.id);
      rowIds.add(id);
      if (node.children && node.children.length > 0) {
        visit(node.children, shaded && node.expanded !== false);
      } else if (shaded) {
        altShading.set(id, leafIndex % 2 === 0);
        leafIndex++;
      }
    }
  };

  visit(nodes, true);
  return { rowIds, altShading };
}

export interface ResourceTreeNode {
  id: string | number;
  name?: string;
  originalResourceId?: string | number;
  children?: ResourceTreeNode[];
  [key: string]: unknown;
}

export interface PagedResourceTree {
  /** The tree to hand to Bryntum: every group, with only its paged children. */
  tree: ResourceTreeNode[];
  /** Resource ids behind the materialised leaf rows, for events and time ranges. */
  materialisedResourceIds: string[];
  /** Whether rows on screen stop short of the tree, so a scroll has more to load. */
  hasMoreRows: boolean;
  /**
   * Groups the window has not reached, left out of the tree until a scroll pages
   * their rows in. Named here so the collapse sync can tell a group that is
   * simply not loaded yet from one the user closed, and so the expansion sync
   * leaves it alone.
   */
  pendingGroupIds: string[];
}

function isGroupNode(node: ResourceTreeNode): node is ResourceTreeNode & { children: ResourceTreeNode[] } {
  return Array.isArray(node.children);
}

/** Rows the window still has to give out, and whether it ran short. */
interface RowBudget {
  remaining: number;
  exhausted: boolean;
}

/**
 * One window of rows over the whole tree, in tree order: the first `loadedRows`
 * rows are materialised and the rest are left for the next scroll, so the first
 * group fills its page before the next group holds any rows at all. Paging per
 * group instead would spend a page on every group up front, and still leave no
 * way to scroll to the rows of the first one.
 *
 * A group the window has not reached is left out of the tree and named in
 * `pendingGroupIds`: rendered, it would sit below the loaded rows as a header
 * that opens onto nothing, reading as the end of the list.
 *
 * A group the user collapsed does render, carrying its full resource count:
 * collapsing one — or collapsing all — must leave every group on screen.
 *
 * Only rows on screen are charged to the window: the rows of a group that is
 * closed cost no render, so charging them would leave the viewport short of
 * rows it could fill, and the scroll listener would page in rows nobody can
 * see. A closed group still carries a page of its rows, so its expander has
 * something to open — Bryntum's own marker for a parent whose rows are not
 * loaded (`children: true`) breaks `collapseAll`, which spreads the children of
 * every row it walks.
 */
export function applySequentialRowPaging(
  tree: readonly ResourceTreeNode[],
  loadedRows: number,
  collapsedGroupIds: ReadonlySet<string> = new Set(),
  pageSize: number = RESOURCE_PAGE_SIZE,
  groupRowCaps: ReadonlyMap<string, number> = new Map(),
): PagedResourceTree {
  const materialisedResourceIds: string[] = [];
  const pendingGroupIds: string[] = [];
  const budget: RowBudget = { remaining: Math.max(loadedRows, pageSize), exhausted: false };

  const pageRows = (
    nodes: readonly ResourceTreeNode[],
    rows: RowBudget,
    materialised: string[] | null,
    pending: string[] | null,
  ): ResourceTreeNode[] => {
    const paged: ResourceTreeNode[] = [];

    for (const node of nodes) {
      if (!isGroupNode(node)) {
        if (rows.remaining <= 0) {
          rows.exhausted = true;
          continue;
        }
        rows.remaining -= 1;
        materialised?.push(String(node.originalResourceId ?? node.id));
        paged.push(node);
        continue;
      }

      const id = String(node.id);
      const total = node.children.length;
      const unreached = rows.remaining <= 0 && total > 0;

      if (unreached && !collapsedGroupIds.has(id)) {
        pending?.push(id);
        rows.exhausted = true;
        continue;
      }

      if (collapsedGroupIds.has(id)) {
        // A page of its rows, off screen: free of the window, and carrying no
        // bookings until the group is opened and the rows are charged for real.
        paged.push({
          ...node,
          resourceCount: total,
          expanded: false,
          children: pageRows(
            node.children,
            { remaining: pageSize, exhausted: false },
            null,
            null,
          ),
        });
        continue;
      }

      // A group the user has just opened gives out a page of its rows and no
      // more, however far the window reaches: opening one is a page of its own,
      // so the rest of its rows follow on the next scroll rather than all at
      // once. Its cap is raised a page at a time along with the window.
      const cap = groupRowCaps.get(id);
      if (cap !== undefined && cap < rows.remaining) {
        const capped: RowBudget = { remaining: cap, exhausted: false };
        const children = pageRows(node.children, capped, materialised, pending);
        rows.remaining -= cap - capped.remaining;
        if (capped.exhausted) rows.exhausted = true;
        paged.push({ ...node, resourceCount: total, expanded: true, children });
        continue;
      }

      // Open explicitly: a group the window has only just reached was rendered
      // collapsed until now, and a row Bryntum already holds keeps the state it
      // was given — its rows would be charged to the window with nothing on
      // screen to show for them.
      paged.push({
        ...node,
        resourceCount: total,
        expanded: true,
        children: pageRows(node.children, rows, materialised, pending),
      });
    }

    return paged;
  };

  const paged = pageRows(tree, budget, materialisedResourceIds, pendingGroupIds);

  return {
    tree: paged,
    materialisedResourceIds,
    hasMoreRows: budget.exhausted,
    pendingGroupIds,
  };
}

/** Every group row of the tree, nested groups included. */
export function groupIdsOf(tree: readonly ResourceTreeNode[]): Set<string> {
  const ids = new Set<string>();

  const visit = (nodes: readonly ResourceTreeNode[]): void => {
    for (const node of nodes) {
      if (!isGroupNode(node)) continue;
      ids.add(String(node.id));
      visit(node.children);
    }
  };

  visit(tree);
  return ids;
}

/** The leaf rows held below a node, nested groups included. */
function rowCount(node: ResourceTreeNode): number {
  if (!isGroupNode(node)) return 1;
  return node.children.reduce((total, child) => total + rowCount(child), 0);
}

/**
 * The rows the window has to hold for `groupId` to show a page of its own rows:
 * every row above it, plus its first page.
 *
 * Opening a group the window has not reached would otherwise open it empty, and
 * nothing would ever page its rows in: the rows above it have not been scrolled
 * past, so the scroll is nowhere near the bottom.
 *
 * Rows of a collapsed group are not materialised, so they are not counted.
 */
export function rowsToRevealGroup(
  tree: readonly ResourceTreeNode[],
  groupId: string,
  collapsedGroupIds: ReadonlySet<string> = new Set(),
  pageSize: number = RESOURCE_PAGE_SIZE,
): number {
  let rowsAbove = 0;
  let needed: number | null = null;

  const visit = (nodes: readonly ResourceTreeNode[]): void => {
    for (const node of nodes) {
      if (needed !== null) return;

      if (!isGroupNode(node)) {
        rowsAbove += 1;
        continue;
      }

      const id = String(node.id);
      if (id === groupId) {
        needed = rowsAbove + Math.min(rowCount(node), pageSize);
        return;
      }
      if (collapsedGroupIds.has(id)) continue;
      visit(node.children);
    }
  };

  visit(tree);
  return needed ?? 0;
}

/**
 * Every row at once: no paging, so no group ever holds rows a page has not
 * reached. Collapsed groups keep their loaded children and are simply rendered
 * collapsed, so opening one costs no fetch and no rebuild.
 *
 * This is the shape used when the `lazyLoad` feature flag is off.
 */
export function materialiseAllResourceRows(
  tree: readonly ResourceTreeNode[],
  collapsedGroupIds: ReadonlySet<string> = new Set(),
): PagedResourceTree {
  const materialisedResourceIds: string[] = [];

  const visit = (node: ResourceTreeNode): ResourceTreeNode => {
    if (!isGroupNode(node)) {
      materialisedResourceIds.push(String(node.originalResourceId ?? node.id));
      return node;
    }
    return { ...node, resourceCount: node.children.length, children: node.children.map(visit) };
  };

  return {
    tree: withCollapsedGroups(tree.map(visit), collapsedGroupIds),
    materialisedResourceIds,
    hasMoreRows: false,
    pendingGroupIds: [],
  };
}

/**
 * The same tree with `expanded: false` on the given groups. Loading a tree into
 * the store applies each node's own `expanded`, so a rebuild springs every group
 * the user had collapsed back open — and paging its rows in makes it look as if
 * scrolling expanded an unrelated group. Carrying the collapsed state in the
 * data keeps the rebuilt rows collapsed as they render, rather than collapsing
 * them again afterwards.
 */
export function withCollapsedGroups(
  tree: readonly ResourceTreeNode[],
  collapsedGroupIds: ReadonlySet<string>,
): ResourceTreeNode[] {
  if (collapsedGroupIds.size === 0) return tree as ResourceTreeNode[];

  const apply = (node: ResourceTreeNode): ResourceTreeNode => {
    if (node.children == null) return node;
    const expanded = collapsedGroupIds.has(String(node.id)) ? false : node.expanded;
    if (!isGroupNode(node)) return { ...node, expanded };
    return { ...node, expanded, children: node.children.map(apply) };
  };

  return tree.map(apply);
}



